// PRC-034 / PRC-044: menú de la cuenta de Pricing. Se abre al pulsar el nombre en
// el menú lateral (el de antes de PRC-034): cabecera con nombre, email, plan y
// rol; Cuenta; Ayuda; Cerrar sesión. Solo Pricing.
import React from 'react';
import { Box, ButtonBase, Popover } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import axiosInstance from '../../../config/axiosInstance';
import { getPlatformAdminContext } from '../../platformAdmin/services/platformAdminService';
import { J } from '../journeyTokens';
import { SUPPORT_EMAIL } from '../journeyBilling';

export interface PricingAccountMenuProps {
  // Punto donde se pulsó: el menú se abre hacia arriba desde ahí.
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  userName: string;
  userEmail: string;
  planLabel: string | null;
  isAdmin: boolean;
  firstStepsPending: boolean;
  progress?: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  zIndex?: number;
}

const initials = (name: string) => name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'U';

const Avatar: React.FC<{ name: string }> = ({ name }) => (
  <Box component="span" aria-hidden sx={{ width: 36, height: 36, flex: '0 0 36px', borderRadius: '10px', background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)', color: '#0f172a', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {initials(name)}
  </Box>
);

const PricingAccountMenu: React.FC<PricingAccountMenuProps> = ({
  anchorPosition, onClose, userName, userEmail, planLabel, isAdmin, firstStepsPending, progress, onNavigate, onLogout, zIndex,
}) => {
  const open = Boolean(anchorPosition);

  // Se piden al abrir el menú, no al cargar cada página (PRC-026).
  const { data: platformAdmin } = useQuery({
    queryKey: ['platform-admin-context'],
    queryFn: getPlatformAdminContext,
    enabled: open,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const { data: team } = useQuery({
    queryKey: ['pricing-team-context'],
    queryFn: async () => (await axiosInstance.get<{ active_members_count: number; max_users: number }>('/pricing-team/context')).data,
    enabled: open && isAdmin,
    retry: false,
    staleTime: 60 * 1000,
  });

  const go = (route: string) => { onClose(); onNavigate(route); };
  // PRC-045: todo alineado a la izquierda (ButtonBase centra por defecto); las cifras, con ml: 'auto', a la derecha.
  const menuItemSx = {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', minHeight: 38, px: '10px', width: '100%', borderRadius: '8px',
    color: '#e2e8f0', fontFamily: J.font, fontSize: 14, fontWeight: 600, textAlign: 'left',
    '& svg': { fontSize: 18, color: J.muted, flex: '0 0 18px' },
    '&:hover, &.Mui-focusVisible': { bgcolor: 'rgba(56,189,248,.12)' },
  };
  const sectionSx = { display: 'block', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: J.muted, p: '8px 10px 4px' };
  const roleLabel = isAdmin ? 'Administrador' : 'Usuario';

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      onClose={onClose}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      sx={zIndex ? { zIndex } : undefined}
      slotProps={{ paper: { sx: { width: 280, bgcolor: '#0f1b2e', backgroundImage: 'none', border: '1px solid #233149', borderRadius: '14px', boxShadow: '0 24px 60px rgba(2,6,23,.65)', p: 1 } } }}
    >
      <Box role="menu" aria-label="Menú de la cuenta" sx={{ display: 'flex', flexDirection: 'column', gap: '2px', fontFamily: J.font }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '10px 10px 12px', borderBottom: `1px solid ${J.border}`, mb: '6px' }}>
          <Avatar name={userName} />
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box component="b" sx={{ fontSize: 14, color: J.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</Box>
            <Box component="span" sx={{ fontSize: 12, color: J.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{userEmail}</Box>
            <Box component="span" sx={{ mt: '6px', alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, color: J.okText, border: '1px solid rgba(74,222,128,.35)', bgcolor: 'rgba(74,222,128,.08)', borderRadius: 999, px: '8px', py: '2px' }}>
              {planLabel ? `${planLabel} · ${roleLabel}` : roleLabel}
            </Box>
          </Box>
        </Box>

        <Box component="span" sx={sectionSx}>Cuenta</Box>
        <ButtonBase role="menuitem" autoFocus onClick={() => go('/profile')} sx={menuItemSx}><PersonOutlineRoundedIcon />Mi perfil</ButtonBase>
        {isAdmin && <ButtonBase role="menuitem" onClick={() => go('/subscription')} sx={menuItemSx}><CreditCardRoundedIcon />Mi suscripción</ButtonBase>}
        {isAdmin && (
          <ButtonBase role="menuitem" onClick={() => go('/team')} sx={menuItemSx}>
            <GroupOutlinedIcon />Usuarios del equipo
            {team && <Box component="span" sx={{ ml: 'auto', fontSize: 11, color: J.muted }}>{team.active_members_count} de {team.max_users}</Box>}
          </ButtonBase>
        )}
        {platformAdmin?.is_platform_admin === true && (
          <ButtonBase role="menuitem" onClick={() => go('/platform-admin')} sx={menuItemSx}><AdminPanelSettingsOutlinedIcon />Administrador PROA</ButtonBase>
        )}

        <Box component="span" sx={sectionSx}>Ayuda</Box>
        {firstStepsPending && (
          <ButtonBase role="menuitem" onClick={() => go('/primeros-pasos')} sx={menuItemSx}>
            <FlagOutlinedIcon />Primeros pasos
            {progress && <Box component="span" sx={{ ml: 'auto', fontSize: 11, color: J.muted }}>{progress}</Box>}
          </ButtonBase>
        )}
        <ButtonBase role="menuitem" component="a" href={`mailto:${SUPPORT_EMAIL}`} onClick={onClose} sx={menuItemSx}><MailOutlineRoundedIcon />Escribir a soporte</ButtonBase>

        <Box aria-hidden sx={{ height: '1px', bgcolor: J.border, m: '6px 4px' }} />
        <ButtonBase role="menuitem" onClick={() => { onClose(); onLogout(); }} sx={{ ...menuItemSx, color: '#fca5a5', '& svg': { ...menuItemSx['& svg'], color: '#fca5a5' } }}>
          <LogoutRoundedIcon />Cerrar sesión
        </ButtonBase>
      </Box>
    </Popover>
  );
};

export default PricingAccountMenu;
